import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MicIcon from '@mui/icons-material/Mic';

interface AudioPlayerProps {
  src: string;
  accent?: string;
  autoPlay?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, accent = '#c0392b', autoPlay = false }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = 'metadata';
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => { setDuration(audio.duration); setLoadError(false); });
    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
    });
    audio.addEventListener('ended', () => { setPlaying(false); setProgress(0); setCurrentTime(0); });
    audio.addEventListener('error', () => setLoadError(true));

    if (autoPlay) {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    }

    return () => { audio.pause(); audio.src = ''; };
  }, [src, autoPlay]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play().then(() => setPlaying(true)).catch(() => setLoadError(true)); }
  };

  const fmt = (s: number) => {
    if (!s || !isFinite(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  if (loadError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, opacity: 0.5 }}>
        <MicIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.65rem" }}>Unavailable</Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={toggle}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', minWidth: 0 }}
    >
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%',
        backgroundColor: playing ? accent : '#f2ede6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, transition: 'all 0.2s',
      }}>
        {playing
          ? <PauseIcon sx={{ fontSize: 14, color: '#faf8f5' }} />
          : <PlayArrowIcon sx={{ fontSize: 14, color: accent }} />
        }
      </Box>
      {/* Progress bar */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ height: 3, backgroundColor: '#ddd5cc', borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${progress}%`, backgroundColor: accent, transition: 'width 0.1s linear' }} />
        </Box>
      </Box>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.6rem', color: '#9a918e', flexShrink: 0, minWidth: 28, textAlign: 'right' }}>
        {playing ? fmt(currentTime) : fmt(duration)}
      </Typography>
    </Box>
  );
};

export default AudioPlayer;
